import { computed, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";
import type { LocationQuery } from "vue-router";
import { useAccountsStore } from "@stores/accounts";
import { useSurveysStore } from "@/stores/surveys";
import { useCurrentUserStore } from "@/stores/currentUser";
import type { FilterConfig } from "@/components/episto-ui/filter-dropdown";
import { createFilters } from "@/utils/composables/filters/createFilters";
import {
  useFiltersUrlSync,
  hasUrlFilterParams,
  arrayParam,
  flagParam,
  stringParam,
  pageParam,
  type UrlFiltersState,
} from "@/utils/composables/filters/useFiltersUrlSync";

const urlParams = {
  status: arrayParam(),
  accounts: arrayParam(Number),
  studyType: arrayParam(),
  mineOnly: flagParam(),
  freemiumDemo: flagParam(),
  search: stringParam(),
  page: pageParam(),
};

type SurveysUrlState = UrlFiltersState<typeof urlParams>;

export const hasSurveysUrlFilters = (query: LocationQuery) => hasUrlFilterParams(query, urlParams);

export const useSurveysFilters = () => {
  const accountsStore = useAccountsStore();
  const surveysStore = useSurveysStore();
  const currentUserStore = useCurrentUserStore();

  const { accounts } = storeToRefs(accountsStore);
  const {
    surveysMineOnlyFilter,
    surveysAccountsFilter,
    surveysStatusFilter,
    surveysStudyTypeFilter,
    surveysShowFreemiumDemoFilter,
    surveysSearchQuery,
    currentPage,
  } = storeToRefs(surveysStore);
  const { toggleMineOnlyFilter, toggleShowFreemiumDemoFilter, clearFilters, search, setSearchQuery } = surveysStore;
  const { isAdmin, isEpisto } = storeToRefs(currentUserStore);

  const accountOptions = computed(
    () => accounts.value?.map(({ name, id }) => ({ label: name, value: id })) || [],
  );

  const filters = createFilters({
    filters: [
      {
        id: "status",
        label: "Status",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Review", value: "review" },
          { label: "Live", value: "live" },
          { label: "Paused", value: "paused" },
          { label: "Complete", value: "complete" },
        ],
        model: surveysStatusFilter,
      },
      {
        id: "studyType",
        label: "Study type",
        options: [
          { label: "Quali", value: "ai_chat" },
          { label: "Quanti", value: "quanti" },
        ],
        model: surveysStudyTypeFilter,
      },
      {
        id: "accounts",
        label: "Account",
        hasSearch: true,
        options: accountOptions,
        model: surveysAccountsFilter,
        when: () => isAdmin.value || accounts.value.length > 1,
      },
    ],
    search,
    clear: clearFilters,
  });

  const filterConfigs = computed<FilterConfig[]>(() => {
    const [statusConfig, studyTypeConfig, ...accountsConfig] = filters.filterConfigs.value;

    const configs: FilterConfig[] = [
      statusConfig,
      studyTypeConfig,
      {
        id: "mineOnly",
        label: "Owner",
        hasSearch: false,
        options: [{ label: "Me", value: true }],
        selectedValues: surveysMineOnlyFilter.value ? [true] : [],
      },
      ...accountsConfig,
    ];

    return configs;
  });

  const freemiumToggle = computed(() => ({
    visible: isEpisto.value,
    label: "Hide freemium demos",
    hidden: !surveysShowFreemiumDemoFilter.value,
    toggle: () => toggleShowFreemiumDemoFilter(),
  }));

  const activeFiltersCount = computed(
    () =>
      filters.activeFiltersCount.value +
      (surveysMineOnlyFilter.value ? 1 : 0) +
      (surveysShowFreemiumDemoFilter.value ? 1 : 0),
  );

  const handleFilterChange = (filterId: string, value: string | boolean | number) => {
    if (filterId === "mineOnly") toggleMineOnlyFilter();
    else if (filterId === "showFreemiumDemo") toggleShowFreemiumDemoFilter();
    else filters.handleFilterChange(filterId, value);
  };

  const serializeUrlState = (): SurveysUrlState => ({
    status: surveysStatusFilter.value,
    accounts: surveysAccountsFilter.value,
    studyType: surveysStudyTypeFilter.value,
    mineOnly: surveysMineOnlyFilter.value,
    freemiumDemo: surveysShowFreemiumDemoFilter.value,
    search: surveysSearchQuery.value,
    page: currentPage.value,
  });

  const applyUrlState = (state: SurveysUrlState) => {
    surveysStatusFilter.value = state.status;
    surveysAccountsFilter.value = state.accounts;
    surveysStudyTypeFilter.value = state.studyType;
    surveysMineOnlyFilter.value = state.mineOnly;
    surveysShowFreemiumDemoFilter.value = state.freemiumDemo;
    setSearchQuery(state.search);
    currentPage.value = state.page;
  };

  const { syncUrl, restoreFromUrl } = useFiltersUrlSync({
    params: urlParams,
    serialize: serializeUrlState,
    apply: applyUrlState,
    onExternalChange: async (state) => {
      applyUrlState(state);
      await search(currentPage.value);
    },
  });

  const restored = restoreFromUrl();

  onMounted(async () => {
    if (restored) await search(currentPage.value);
  });

  watch(
    [
      surveysStatusFilter,
      surveysAccountsFilter,
      surveysStudyTypeFilter,
      surveysMineOnlyFilter,
      surveysShowFreemiumDemoFilter,
      surveysSearchQuery,
      currentPage,
    ],
    useDebounceFn(syncUrl, 300),
    { deep: true },
  );

  return { ...filters, filterConfigs, activeFiltersCount, handleFilterChange, freemiumToggle };
};
